import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, PhoneCall, Headset, Clock, Loader2 } from "lucide-react";

const agents = [
  { initials: "SM", status: "On a call" },
  { initials: "DK", status: "Onboarding" },
  { initials: "AL", status: "On a call" },
];

export default function ConfirmationPage() {
  const [phase, setPhase] = useState("loading");

  useEffect(() => {
    const t = setTimeout(() => setPhase("queue"), 5000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div data-testid="confirmation-page" className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="confirmation-logo-link" className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </span>
          <span className="font-heading font-bold text-xl text-gray-900">AI Central</span>
        </Link>
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400">Account Activation</span>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 py-12">
        {phase === "loading" ? (
          <div data-testid="details-loading-card" className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-10 text-center shadow-sm">
            <div className="relative w-24 h-24 mx-auto flex items-center justify-center">
              <span className="absolute inset-0 rounded-full bg-blue-500/20" style={{ animation: "pulse-ring 1.8s ease-out infinite" }} />
              <div className="relative w-20 h-20 rounded-2xl bg-blue-600 shadow-lg shadow-blue-600/30 flex items-center justify-center">
                <Loader2 className="w-9 h-9 text-white animate-spin" />
              </div>
            </div>
            <h1 className="font-heading text-2xl font-bold text-gray-900 mt-8">Preparing Your Account</h1>
            <p className="text-sm text-gray-500 mt-3 font-mono">Loading your details...</p>
            <div className="mt-8 w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 rounded-full" style={{ animation: "search-progress 5s linear forwards" }} />
            </div>
            <p className="mt-4 text-xs text-gray-400">This will only take a moment</p>
          </div>
        ) : (
          <div data-testid="queue-card" className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-10 text-center shadow-sm animate-fade-up">
            <div className="relative w-24 h-24 mx-auto flex items-center justify-center">
              <span className="absolute inset-0 rounded-full bg-emerald-500/20" style={{ animation: "pulse-ring 2s ease-out infinite" }} />
              <span className="absolute inset-0 rounded-full bg-emerald-500/10" style={{ animation: "pulse-ring 2s ease-out 1s infinite" }} />
              <div className="relative w-20 h-20 rounded-full bg-emerald-600 shadow-lg shadow-emerald-600/30 flex items-center justify-center">
                <PhoneCall className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="font-heading text-2xl font-bold text-gray-900 mt-8">You're in the Queue</h1>
            <p data-testid="queue-message" className="text-sm text-gray-600 mt-4 leading-relaxed">
              All our agents are currently busy onboarding new clients — one will be with you shortly.
              As soon as an agent is available, you'll receive a call. Please be patient and keep your phone nearby.
            </p>

            <div data-testid="queue-agents" className="mt-7 grid grid-cols-3 gap-3">
              {agents.map((a, i) => (
                <div key={i} className="bg-gray-50 border border-gray-200 rounded-xl py-4 px-2">
                  <div className="relative w-10 h-10 mx-auto">
                    <span className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">
                      {a.initials}
                    </span>
                    <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500 border-2 border-white" />
                    </span>
                  </div>
                  <p className="mt-2 flex items-center justify-center gap-1 text-[10px] font-semibold text-gray-500">
                    <Headset className="w-3 h-3" />
                    {a.status}
                  </p>
                </div>
              ))}
            </div>

            <div data-testid="queue-eta" className="mt-7 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-bold px-5 py-2.5 rounded-full">
              <Clock className="w-4 h-4" />
              Estimated wait time: ~3 minutes
            </div>

            <p className="mt-6 text-xs text-gray-400">
              Feel free to keep exploring while you wait —{" "}
              <Link to="/" data-testid="queue-back-home-link" className="text-blue-600 font-semibold hover:text-blue-700">
                back to home
              </Link>
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
